import { ReactNode, useEffect, useState } from 'react';
import { TokenAccessor } from '../../types/API';
import {
  initAssignmentSession,
  InitAssignmentSessionResponse,
  initFreeExerciseSessionScorableMaterial,
  InitFreeExerciseSessionResponse,
} from '../exercise-manager/api/exercises-api';
import ConfirmationDialog from './ConfirmationDialog';
import {
  ConfirmationModalTranslations,
  ScorableAssignmentLtiUserMaterial,
  ScorableFreeExerciseLtiUserMaterial,
} from './types';

type ConfirmationProps = {
  material: ScorableAssignmentLtiUserMaterial | ScorableFreeExerciseLtiUserMaterial;
  tokenAccessor: TokenAccessor;
  onConfirm: () => void;
  translations: ConfirmationModalTranslations;
  isOwner: boolean;
  id: string;
  studioGroup: string;
  tags: ReactNode;
};

export default function Confirmation({
  id,
  material,
  tokenAccessor,
  onConfirm,
  translations,
  isOwner,
  studioGroup,
  tags,
}: ConfirmationProps): JSX.Element | null {
  const [session, setSession] = useState<InitAssignmentSessionResponse | InitFreeExerciseSessionResponse | null>(null);

  useEffect(() => {
    async function initConfirmation() {
      const token = await tokenAccessor.tokenGetter();

      const sessionData: InitAssignmentSessionResponse | InitFreeExerciseSessionResponse =
        !isOwner && material.assignment
          ? await initAssignmentSession(token, material.module, material.assignment.id, studioGroup)
          : await initFreeExerciseSessionScorableMaterial(token, material.module, material.id, studioGroup);

      // set phase to start when assignment is not started yet
      if (sessionData.resumable === false && sessionData.result === undefined) {
        return onConfirm();
      }

      // set phase to start when assignment or free exercise is started but not finished yet
      if (sessionData.resumable === true && (!('isExpired' in sessionData) || !sessionData.isExpired)) {
        return onConfirm();
      }

      setSession(sessionData);
    }

    initConfirmation();
  }, [tokenAccessor, material, onConfirm, isOwner, studioGroup]);

  if (session) {
    // Will be opened by onConfirm
    if (!session.result || session.resumable === true) return null;

    return (
      <ConfirmationDialog
        id={id}
        material={material}
        onConfirm={onConfirm}
        translations={translations}
        session={session}
        isOwner={isOwner}
        tags={tags}
      />
    );
  }

  return null;
}
